import { lazy, Suspense, type ReactNode } from 'react';
import type { RouteObject } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import RequireModule from '@/core/modules/RequireModule';
import { Modules } from '@/core/modules/moduleKeys';

// Annova Me is its own chunk (NFR-04): the portal's pages load only when someone opens /me.
const MeLayout = lazy(() => import('./MeLayout'));
const MeHomePage = lazy(() => import('./pages/MeHomePage'));
const MePayPage = lazy(() => import('./pages/MePayPages').then((m) => ({ default: m.MePayPage })));
const MePayslipPage = lazy(() => import('./pages/MePayPages').then((m) => ({ default: m.MePayslipPage })));
const MePayToolsPage = lazy(() => import('./pages/MePayPages').then((m) => ({ default: m.MePayToolsPage })));
const MeLeavePage = lazy(() => import('./pages/MeLeavePage'));
const MeMoneyPage = lazy(() => import('./pages/MeMoneyPage'));
const MeTeamPage = lazy(() => import('./pages/MeTeamPage'));
const MeApprovalsPage = lazy(() => import('./pages/MeApprovalsPage'));
const MeDocumentsPage = lazy(() => import('./pages/MeDocumentsPage'));
const MeGrowPage = lazy(() => import('./pages/MeGrowPage'));
const MeHelpPage = lazy(() => import('./pages/MeHelpPage'));
const MeProfilePage = lazy(() => import('./pages/MeMorePages').then((m) => ({ default: m.MeProfilePage })));
const MeMorePage = lazy(() => import('./pages/MeMorePages').then((m) => ({ default: m.MeMorePage })));
const MyHrRedirect = lazy(() => import('./MyHrRedirect'));
const KioskPage = lazy(() => import('./pages/KioskPage'));
const VerifyPage = lazy(() => import('./pages/PublicPages').then((m) => ({ default: m.VerifyPage })));
const WelcomePage = lazy(() => import('./pages/PublicPages').then((m) => ({ default: m.WelcomePage })));

const wait = (element: ReactNode) => (
  <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}>{element}</Suspense>
);

/** Annova Me, inside sign-in but outside the main layout: a mobile-first frame of its own. No permission — the module only. */
export const meRoutes: RouteObject[] = [
  { path: 'my-hr/:view?', element: wait(<MyHrRedirect />) },
  { path: 'me/kiosk', element: wait(<RequireModule module={Modules.Hr}><KioskPage /></RequireModule>) },
  {
    path: 'me',
    element: wait(<RequireModule module={Modules.Hr}><MeLayout /></RequireModule>),
    children: [
      { index: true, element: wait(<MeHomePage />) },
      { path: 'pay', element: wait(<MePayPage />) },
      { path: 'pay/tools/:tool?', element: wait(<MePayToolsPage />) },
      { path: 'pay/:payslipId/:tab?', element: wait(<MePayslipPage />) },
      { path: 'leave/:view?', element: wait(<MeLeavePage />) },
      { path: 'money/:view?', element: wait(<MeMoneyPage />) },
      { path: 'team/:view?', element: wait(<MeTeamPage />) },
      { path: 'approvals', element: wait(<MeApprovalsPage />) },
      { path: 'documents/:view?/:id?', element: wait(<MeDocumentsPage />) },
      { path: 'grow/:view?', element: wait(<MeGrowPage />) },
      { path: 'help/:view?', element: wait(<MeHelpPage />) },
      { path: 'profile/:view?', element: wait(<MeProfilePage />) },
      { path: 'more', element: wait(<MeMorePage />) },
    ],
  },
];

/** The anonymous pages: a document's QR code and a new starter's link. */
export const mePublicRoutes: RouteObject[] = [
  { path: '/verify/:token', element: wait(<VerifyPage />) },
  { path: '/welcome/:token', element: wait(<WelcomePage />) },
];
